import { inject, Service } from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { Validators } from '@angular/forms';
import type { OnyxAddress, OnyxRoutePoint } from '@onyx/angular';
import {
  onyxLengthValidator,
  onyxTextareaLengthValidator,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { isObject, mergeWith, omit, uniq } from 'lodash';
import type { MergeDeep } from 'type-fest';
import { v7 as uuidv7 } from 'uuid';
import { BusinessType } from '../../../../auth/common/enums/business/business-type';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import { DocumentsFormComponent } from '../../../../common/components/forms/documents-form/documents-form.component';
import { NoteFormComponent } from '../../../../common/components/forms/note-form/note-form.component';
import { ParametersFormComponent } from '../../../../common/components/forms/parameters-form/parameters-form.component';
import { GoodsSizeType } from '../../../../common/enums/goods-size-type';
import type {
  FleetType,
  FleetValidationByType,
} from '../../../../common/interfaces/validation/fleet-validation';
import type { ValidationField } from '../../../../common/interfaces/validation/validation-field';
import type { PreferencesService } from '../../../../common/services/preferences.service';
import { OrderBasicInformationFormComponent } from '../../order-form/order-basic-information-form/order-basic-information-form.component';
import { OrderPointBaseFormComponent } from '../../order-form/order-points-form/order-point-form/order-point-base-form/order-point-base-form.component';
import { OrderPointsFormComponent } from '../../order-form/order-points-form/order-points-form.component';
import { OrderDispatch } from '../enums/order-dispatch';
import { OrderPointCategory } from '../enums/order-point-category';
import { OrderPointType } from '../enums/order-point-type';
import type { Order, OrderRoute } from '../interfaces/order';

export type OrderFormGroup = ReturnType<typeof OrderFormHelper.buildForm>;
export type OrderForm = ReturnType<OrderFormGroup['getRawValue']>;

export type OrderFormDto = MergeDeep<
  Omit<OrderForm, 'basicInformation'>,
  {
    basicInformation: Omit<
      OrderForm['basicInformation'],
      'loadedSemiTrailer' | 'isDropped' | 'pickupPointUuid' | 'dropPointUuid'
    > & {
      loadedSemiTrailer: {
        uuid: string | null;
        registrationNumber: string | null;
        isDropped: boolean | null;
        pickupPointUuid: string | null;
        dropPointUuid: string | null;
      };
    };
  }
>;

export type OrderLoadingPointFormGroup = ReturnType<
  typeof OrderFormHelper.buildLoadingPointForm
>;
export type OrderLoadingPointForm = ReturnType<
  OrderLoadingPointFormGroup['getRawValue']
>;

export type OrderLoadingPointGoodFormGroup = ReturnType<
  typeof OrderFormHelper.buildLoadingPointGoodForm
>;
export type OrderLoadingPointGoodForm = ReturnType<
  OrderLoadingPointGoodFormGroup['getRawValue']
>;

@Service()
export class OrderFormHelper {
  private readonly authStore = inject(AuthStore);

  public getDispatches(): OrderDispatch[] {
    return {
      [BusinessType.SENDER]: [OrderDispatch.DRAFT, OrderDispatch.GLOBAL],
      [BusinessType.CARRIER]: [OrderDispatch.DRAFT, OrderDispatch.FLEET],
      [BusinessType.LOGISTIC_OPERATOR]: [
        OrderDispatch.DRAFT,
        OrderDispatch.FLEET,
        OrderDispatch.GLOBAL,
        OrderDispatch.FLEET_GLOBAL,
      ],
    }[this.authStore.business()!.type];
  }

  public getDefaultDispatch(): OrderDispatch {
    return this.getDispatches().find(
      (dispatch) => dispatch !== OrderDispatch.DRAFT,
    )!;
  }

  public static fromDto(dto: Order, defaultDispatch: OrderDispatch): OrderForm {
    const loadedSemiTrailer = dto.basicInformation.loadedSemiTrailer;
    return {
      dispatch:
        dto.dispatch === OrderDispatch.DRAFT ? defaultDispatch : dto.dispatch,
      basicInformation: {
        ...dto.basicInformation,
        sender: dto.basicInformation.sender.uuid,
        ourBranch: dto.basicInformation.ourBranch.uuid,
        senderBranch: dto.basicInformation.senderBranch.uuid,
        loadedSemiTrailer:
          loadedSemiTrailer?.uuid ??
          loadedSemiTrailer?.registrationNumber ??
          null,
        isDropped: loadedSemiTrailer?.isDropped ?? null,
        pickupPointUuid: loadedSemiTrailer?.pickupPointUuid ?? null,
        dropPointUuid: loadedSemiTrailer?.dropPointUuid ?? null,
      },
      parameters: {
        ...dto.parameters,
        temperatureRange: {
          from: dto.parameters.temperatureRange?.from ?? null,
          to: dto.parameters.temperatureRange?.to ?? null,
        },
        otherVehicleParameters:
          dto.parameters.otherVehicleParameters?.map(
            (parameter) => parameter.uuid,
          ) ?? null,
        otherDriverPermissions:
          dto.parameters.otherDriverPermissions?.map(
            (parameter) => parameter.uuid,
          ) ?? null,
      },
      documents: dto.documents,
      note: { content: dto.note?.content ?? null },
      points: dto.points.map((point) => {
        if (point.category !== OrderPointCategory.LOADING) return point;
        return {
          ...point,
          goods: point.goods.map((good): OrderLoadingPointGoodForm => ({
            ...good,
            type: good.type?.uuid ?? good.typeName,
            unit: good.unit.uuid,
            size: good.size ?? {
              type: null,
              loadingMeters: null,
              requiresCoilWell: null,
              length: null,
              width: null,
              height: null,
              volume: null,
            },
          })),
        };
      }),
      route: dto.route ? omit(dto.route, ['author', 'updatedAt']) : null,
    };
  }

  public static buildLoadingPointForm(
    fb: NonNullableFormBuilder,
    timeWindowsCount = 1,
    goodsCount = 1,
  ) {
    return fb.group({
      uuid: fb.control(uuidv7()),
      category: fb.control(OrderPointCategory.LOADING as const),
      type: fb.control(OrderPointType.LOADING as const),
      address: fb.control<OnyxAddress | null>(null, [Validators.required]),
      referenceNumber: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      serviceTime: fb.control<number | null>(null, [Validators.required]),
      driverNote: fb.control<string | null>(null, [
        onyxTextareaLengthValidator,
      ]),
      timeWindows: OrderPointBaseFormComponent.buildTimeWindowsForm(
        fb,
        timeWindowsCount,
      ),
      goods: fb.array(
        Array.from({ length: goodsCount }).map(() =>
          OrderFormHelper.buildLoadingPointGoodForm(fb),
        ),
      ),
      route: fb.control<OnyxRoutePoint | null>(null),
    });
  }

  public static buildLoadingPointGoodForm(fb: NonNullableFormBuilder) {
    const uuid = uuidv7();
    return fb.group({
      uuid: fb.control(uuid),
      type: fb.control<string | null>(null, [Validators.required]),
      referenceNumber: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      unit: fb.control<string | null>(null, [Validators.required]),
      quantity: fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
      ]),
      totalWeight: fb.control<number | null>(null, [
        Validators.required,
        Validators.min(Number.EPSILON),
      ]),
      size: fb.group({
        type: fb.control<GoodsSizeType | null>(GoodsSizeType.LOADING_METERS, [
          Validators.required,
        ]),
        loadingMeters: fb.control<number | null>(null, [
          Validators.required,
          Validators.min(Number.EPSILON),
        ]),
        requiresCoilWell: fb.control<boolean | null>(null),
        length: fb.control<number | null>(null, [
          Validators.required,
          Validators.min(Number.EPSILON),
        ]),
        width: fb.control<number | null>(null, [
          Validators.required,
          Validators.min(Number.EPSILON),
        ]),
        height: fb.control<number | null>(null, [
          Validators.required,
          Validators.min(Number.EPSILON),
        ]),
        volume: fb.control<number | null>(null, [
          Validators.required,
          Validators.min(Number.EPSILON),
        ]),
      }),
      unitedNationsNumber: fb.control<string | null>(null, [
        onyxLengthValidator(4),
      ]),
      statisticalNumber: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
    });
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    authStore: AuthStore,
    preferencesService: PreferencesService,
    dispatch: OrderDispatch,
  ) {
    return fb.group({
      dispatch: fb.control(dispatch, [Validators.required]),
      basicInformation: OrderBasicInformationFormComponent.buildForm(
        fb,
        authStore,
        preferencesService,
      ),
      parameters: ParametersFormComponent.buildForm('order', fb),
      documents: DocumentsFormComponent.buildForm(fb),
      note: NoteFormComponent.buildForm(fb),
      points: OrderPointsFormComponent.buildForm(fb),
      route: fb.control<Omit<OrderRoute, 'author' | 'updatedAt'> | null>(null),
    });
  }

  public static getValidation(
    fleetValidation: FleetValidationByType,
    vehicleTypes: FleetType[],
  ): FleetValidationByType[FleetType] {
    return mergeWith(
      {},
      ...vehicleTypes.map((type) => fleetValidation[type]),
      (object: any, source: any) => {
        if (
          isObject(object) &&
          isObject(source) &&
          'type' in object &&
          'type' in source
        ) {
          object = object as ValidationField;
          source = source as ValidationField;

          const TYPE_PRIORITY: ValidationField['type'][] = [
            'forbidden',
            'conditional',
            'optional',
            'optional-in-list',
            'required',
            'required-in-list',
            'required-in-range',
          ] as const;

          const objectTypePriority = TYPE_PRIORITY.indexOf(object.type);
          const sourceTypePriority = TYPE_PRIORITY.indexOf(source.type);

          const result =
            sourceTypePriority > objectTypePriority ? source : object;
          if ('values' in source) {
            if ('values' in object) {
              result.values = uniq([...object.values, ...source.values]);
            } else {
              result.values = source.values;
            }
          }

          return result;
        }

        return undefined;
      },
    );
  }
}
